/**
 * Utility functions for Excel file handling and processing
 */

import * as XLSX from 'xlsx';

// Constants for validation
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_EXCEL_EXTENSIONS = ['.xlsx', '.xls', '.xlsm'];
const VALID_EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12'
];

export interface ExcelSheetInfo {
  name: string;
  rowCount: number;
  columnCount: number;
}

export interface ExcelProcessingResult {
  isValid: boolean;
  error?: string;
  sheets?: ExcelSheetInfo[];
  warnings?: string[];
}

export interface ExcelToCSVResult {
  isValid: boolean;
  error?: string;
  csvContent?: string;
  sheetInfo?: ExcelSheetInfo;
}

/**
 * Validates file type to ensure it's an Excel file
 * @param file - The file to validate
 * @returns string error message or null if valid
 */
const validateExcelFileType = (file: File): string | null => {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  
  // Check file extension
  if (!VALID_EXCEL_EXTENSIONS.includes(fileExtension)) {
    return `Please upload an Excel file. The selected file appears to be a ${fileExtension} file, but we need a .xlsx, .xls, or .xlsm file for plot map data.`;
  }
  
  // Check MIME type (note: some systems don't set Excel MIME type correctly, so we're lenient)
  if (file.type && !VALID_EXCEL_MIME_TYPES.includes(file.type) && file.type !== 'application/octet-stream') {
    return `The selected file doesn't appear to be an Excel file. Please ensure you're uploading a .xlsx, .xls, or .xlsm file containing your plot data.`;
  }
  
  return null;
};

/**
 * Validates file size to ensure it's reasonable for processing
 * @param file - The file to validate
 * @returns string error message or null if valid
 */
const validateExcelFileSize = (file: File): string | null => {
  if (file.size === 0) {
    return "The selected file is empty. Please upload an Excel file that contains your plot data.";
  }
  
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `The selected file is ${fileSizeMB}MB, which is too large. Please upload an Excel file smaller than ${MAX_FILE_SIZE_MB}MB. Consider removing unnecessary worksheets or splitting large datasets.`;
  }
  
  return null;
};

/**
 * Detects if a file is an Excel file based on extension
 * @param file - The file to check
 * @returns boolean indicating if the file is an Excel file
 */
export const isExcelFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  return VALID_EXCEL_EXTENSIONS.includes(fileExtension);
};

/**
 * Reads an Excel file and returns information about its sheets
 * @param file - The Excel file to process
 * @returns Promise<ExcelProcessingResult> - Information about sheets in the file
 */
export const readExcelFile = async (file: File): Promise<ExcelProcessingResult> => {
  return new Promise((resolve) => {
    // Pre-processing validation
    const fileTypeError = validateExcelFileType(file);
    if (fileTypeError) {
      resolve({
        isValid: false,
        error: fileTypeError,
      });
      return;
    }
    
    const fileSizeError = validateExcelFileSize(file);
    if (fileSizeError) {
      resolve({
        isValid: false,
        error: fileSizeError,
      });
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        if (!e.target?.result) {
          resolve({
            isValid: false,
            error: "Failed to read the Excel file. Please try again.",
          });
          return;
        }

        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          resolve({
            isValid: false,
            error: "The Excel file contains no worksheets. Please upload a file with at least one worksheet containing plot data.",
          });
          return;
        }

        const sheets: ExcelSheetInfo[] = [];
        const warnings: string[] = [];

        // Process each sheet to get information
        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) {
            warnings.push(`Worksheet "${sheetName}" could not be read and will be skipped.`);
            continue;
          }

          // Get sheet range to determine dimensions
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
          const columnCount = Math.max(0, range.e.c - range.s.c + 1);

          // Count non-blank rows (excluding header)
          let nonBlankRowCount = 0;
          let hasHeader = false;

          // Check each row for non-empty content
          for (let r = range.s.r; r <= range.e.r; r++) {
            let hasContent = false;
            for (let c = range.s.c; c <= range.e.c; c++) {
              const cellAddress = XLSX.utils.encode_cell({ r, c });
              const cell = worksheet[cellAddress];
              if (cell && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              if (!hasHeader) {
                hasHeader = true; // First non-empty row is the header
              } else {
                nonBlankRowCount++; // Count data rows after header
              }
            }
          }

          // Skip completely empty sheets
          if (!hasHeader) {
            warnings.push(`Worksheet "${sheetName}" is empty and will be skipped.`);
            continue;
          }

          sheets.push({
            name: sheetName,
            rowCount: nonBlankRowCount + 1, // Include header in count
            columnCount,
          });
        }

        if (sheets.length === 0) {
          resolve({
            isValid: false,
            error: "The Excel file contains no worksheets with data. Please upload a file with at least one worksheet containing plot data.",
          });
          return;
        }

        resolve({
          isValid: true,
          sheets,
          warnings: warnings.length > 0 ? warnings : undefined,
        });
      } catch (error) {
        resolve({
          isValid: false,
          error: `Failed to process the Excel file. Please ensure it's a valid Excel format and try again. Technical details: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    };

    reader.onerror = () => {
      resolve({
        isValid: false,
        error: "Failed to read the Excel file. This might be due to file corruption or an unsupported format.",
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Converts a specific sheet from an Excel file to CSV format
 * @param file - The Excel file
 * @param sheetName - The name of the sheet to convert
 * @returns Promise<ExcelToCSVResult> - The converted CSV content
 */
export const convertExcelSheetToCSV = async (file: File, sheetName: string): Promise<ExcelToCSVResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        if (!e.target?.result) {
          resolve({
            isValid: false,
            error: "Failed to read the Excel file during conversion.",
          });
          return;
        }

        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        
        if (!workbook.Sheets[sheetName]) {
          resolve({
            isValid: false,
            error: `Worksheet "${sheetName}" not found in the Excel file.`,
          });
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to CSV with options to preserve formatting
        const csvContent = XLSX.utils.sheet_to_csv(worksheet, {
          blankrows: false, // Skip blank rows in output
          strip: false, // Keep trailing field separators for consistency
        });

        if (!csvContent.trim()) {
          resolve({
            isValid: false,
            error: `Worksheet "${sheetName}" is empty or contains no readable data.`,
          });
          return;
        }

        // Get sheet information
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
        const sheetInfo: ExcelSheetInfo = {
          name: sheetName,
          rowCount: Math.max(0, range.e.r - range.s.r + 1),
          columnCount: Math.max(0, range.e.c - range.s.c + 1),
        };

        resolve({
          isValid: true,
          csvContent,
          sheetInfo,
        });
      } catch (error) {
        resolve({
          isValid: false,
          error: `Failed to convert worksheet "${sheetName}" to CSV. Technical details: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    };

    reader.onerror = () => {
      resolve({
        isValid: false,
        error: "Failed to read the Excel file during conversion.",
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Creates a File object from CSV content for compatibility with existing CSV processing
 * @param csvContent - The CSV content string
 * @param originalFileName - The original Excel file name
 * @param sheetName - The sheet name that was converted
 * @returns File object that can be used with existing CSV processing functions
 */
export const createCSVFileFromContent = (
  csvContent: string,
  originalFileName: string,
  sheetName: string
): File => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const csvFileName = originalFileName.replace(/\.(xlsx|xls|xlsm)$/i, `_${sheetName}.csv`);
  return new File([blob], csvFileName, { type: 'text/csv' });
};
